import type { InMemoryCache } from '@apollo/client';
import type { ReadFieldFunction } from '@apollo/client/cache/core/types/common';

import { sendErrorEvent } from '@trello/error-reporting';

import type {
  Action_Type,
  TrelloAction,
  TrelloAppCreator,
  TrelloCardActions,
  TrelloMember,
} from '../generated';
import {
  isEnumString,
  isString,
} from '../plannerCardDataMapping/validateHelpers';
import type { RecursivePartial, TargetModel } from './cacheModelTypes';
import { getObjectIdFromCacheObject } from './getObjectIdFromCacheObject';
import { syncNativeToRestScalars } from './syncNativeToRestScalars';
import { syncNestedModelReference } from './syncNestedModelReference';

const validActionTypes: Action_Type[] = [
  'acceptEnterpriseJoinRequest',
  'addAdminToEnterprise',
  'addAttachmentToCard',
  'addChecklistToCard',
  'addMemberToBoard',
  'addMemberToCard',
  'addMemberToOrganization',
  'addOrganizationToEnterprise',
  'addToEnterprisePluginWhitelist',
  'addToOrganizationBoard',
  'butlerRuleFailedBoard',
  'changeEnterprisePublicBoardVisibility',
  'commentCard',
  'convertToCardFromCheckItem',
  'copyBoard',
  'copyCard',
  'copyInboxCard',
  'copyCommentCard',
  'createBoard',
  'createCard',
  'createInboxCard',
  'createCustomField',
  'createEnterpriseJoinRequest',
  'createList',
  'createOrganization',
  'deactivatedMemberInEnterprise',
  'deactivatedMemberInOrganization',
  'declineEnterpriseJoinRequest',
  'deleteAttachmentFromCard',
  'deleteBoardInvitation',
  'deleteCard',
  'deleteCustomField',
  'deleteList',
  'deleteOrganizationInvitation',
  'disableEnterprisePluginWhitelist',
  'disableIssuingOfConsentTokensInEnterprise',
  'disablePlugin',
  'disablePowerUp',
  'emailCard',
  'enableEnterprisePluginWhitelist',
  'enableIssuingOfConsentTokensInEnterprise',
  'enablePlugin',
  'enablePowerUp',
  'enterpriseAdminViewedPrivateBoard',
  'makeAdminOfBoard',
  'makeNormalMemberOfBoard',
  'makeNormalMemberOfOrganization',
  'makeObserverOfBoard',
  'memberJoinedTrello',
  'moveCardFromBoard',
  'moveCardToBoard',
  'moveInboxCardToBoard',
  'moveListFromBoard',
  'moveListToBoard',
  'reactivatedMemberInEnterprise',
  'reactivatedMemberInOrganization',
  'removeAdminFromEnterprise',
  'removeChecklistFromCard',
  'removeDeprecatedPlugin',
  'removedAllTokensFromOwnedMembers',
  'removedTokensFromMember',
  'removeFromEnterprisePluginWhitelist',
  'removeFromOrganizationBoard',
  'removeMemberFromCard',
  'removeOrganizationFromEnterprise',
  'trelloAddAdminToEnterprise',
  'trelloAddOrganizationToEnterprise',
  'trelloRemoveAdminFromEnterprise',
  'trelloRemoveOrganizationFromEnterprise',
  'unconfirmedBoardInvitation',
  'unconfirmedOrganizationInvitation',
  'updateBoard',
  'updateBoardOrg',
  'updateBoardVisibility',
  'updateCard',
  'updateCheckItemStateOnCard',
  'updateChecklist',
  'updateEnterprise',
  'updateCustomField',
  'updateCustomFieldItem',
  'updateList',
  'updateMember',
  'updateOrganization',
  'withdrawEnterpriseJoinRequest',
  'grantEnterpriseLicense',
  'revokeEnterpriseLicense',
  'grantOrganizationLicenses',
  'updateCardIdList',
  'updateCardClosed',
  'updateCardDue',
  'updateCardDueComplete',
  'updateListClosed',
];

export const fieldMappings = {
  type: {
    validate: (value: unknown) => isEnumString(value, validActionTypes),
    sendValueToSentry: true,
  },
  date: { validate: isString },
};

/**
 * Given native TrelloAction data, writes all action data to the Action model
 * in the Apollo Cache
 * @param incoming A partial TrelloAction model
 * @param cache The cache to write to
 * @param readField The readField function
 */
export const syncTrelloActionToAction = (
  incoming: RecursivePartial<TrelloCardActions>,
  cache: InMemoryCache,
  readField: ReadFieldFunction,
) => {
  try {
    const actionId = getObjectIdFromCacheObject(incoming, readField);
    const action: TargetModel = { type: 'Action', id: actionId };
    syncNativeToRestScalars<TrelloAction>(
      action,
      fieldMappings,
      incoming,
      cache,
      readField,
    );

    const member = readField<TrelloMember>('creator', incoming);

    if (member) {
      const memberId = getObjectIdFromCacheObject(member, readField);
      syncNestedModelReference(
        action,
        {
          model: { type: 'Member', id: memberId },
          fieldName: 'memberCreator',
          idFieldName: 'idMemberCreator',
        },
        cache,
      );
    }

    const appCreator = readField<TrelloAppCreator>('appCreator', incoming);
    if (appCreator) {
      const appCreatorId = getObjectIdFromCacheObject(appCreator, readField);
      syncNestedModelReference(
        action,
        {
          model: { type: 'AppCreator', id: appCreatorId },
          fieldName: 'appCreator',
          idFieldName: 'idAppCreator',
        },
        cache,
      );
    }
  } catch (err) {
    sendErrorEvent(err);
  }
};
